import { useCallback, useEffect, useRef } from "react";

import type { SignedUrlType } from "./types";
import { useSignedUrl } from "./useSignedUrl";

// A short pause before the one-shot retry, giving a transient network blip or brief 5xx a moment
// to clear rather than retrying into the same failure instantly.
const RETRY_DELAY_MS = 3_000;

interface UseSignedUrlWithRetryOptions {
  type?: SignedUrlType;
  onUnrecoverableError: (error: unknown) => void;
  isRetryableError?: (error: unknown) => boolean;
}

/** Reactive signed URL with one automatic, delayed re-mint on a viewer load failure, then gives up.
 * Wraps `useSignedUrl`; `onUnrecoverableError` fires on mount-time failure OR after the single
 * retry (or immediately, unretried, for an error `isRetryableError` classifies as non-retryable,
 * e.g. a password-protected PDF) — never twice for the same episode. */
export function useSignedUrlWithRetry(
  fileId: string | null | undefined,
  { type, onUnrecoverableError, isRetryableError }: UseSignedUrlWithRetryOptions
): {
  url: string;
  isLoading: boolean;
  onViewerLoadError: (error?: unknown) => void;
  onViewerLoadSuccess: () => void;
} {
  const {
    url,
    isLoading,
    error: signedUrlError,
    refresh: refreshSignedUrl,
  } = useSignedUrl(fileId, { type, onInitialError: onUnrecoverableError });

  const onUnrecoverableErrorRef = useRef(onUnrecoverableError);
  onUnrecoverableErrorRef.current = onUnrecoverableError;

  // One retry per open file, spent on a load error and restored only by a subsequent successful
  // document load (not by a re-mint alone) — every re-mint produces a new url, so resetting on
  // url change would let a permanently broken file (corrupt PDF, deleted object) cycle mint →
  // load error → re-mint forever.
  const hasRetriedLoadErrorRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Must stay declared before the effect below that reads the guard — React runs effects in
  // declaration order, so this reset fires before the dependent effect reads the ref.
  useEffect(() => {
    hasRetriedLoadErrorRef.current = false;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, [fileId]);

  // A retry's re-mint can itself reject (e.g. network blip) — that failure sets `signedUrlError`
  // without ever calling onInitialError (which is mount-failure-only), so this is the only place
  // that observes it.
  useEffect(() => {
    if (signedUrlError && hasRetriedLoadErrorRef.current) {
      onUnrecoverableErrorRef.current(signedUrlError);
    }
  }, [signedUrlError]);

  const onViewerLoadError = useCallback(
    (viewerError?: unknown) => {
      const isRetryable = isRetryableError ? isRetryableError(viewerError) : true;
      if (!isRetryable || hasRetriedLoadErrorRef.current) {
        onUnrecoverableErrorRef.current(viewerError);
        return;
      }
      hasRetriedLoadErrorRef.current = true;
      retryTimeoutRef.current = setTimeout(() => {
        refreshSignedUrl();
      }, RETRY_DELAY_MS);
    },
    [isRetryableError, refreshSignedUrl]
  );

  const onViewerLoadSuccess = useCallback(() => {
    hasRetriedLoadErrorRef.current = false;
  }, []);

  return { url, isLoading, onViewerLoadError, onViewerLoadSuccess };
}
